import React, { useEffect, useState } from "react";
import { FiMapPin } from "react-icons/fi";
import api from "../../../../services/axios";
import { FleetSession } from "../../../../interfaces/Equipament";

/** "8h 15min" — minutos crus não respondem "quanto essa máquina foi usada". */
const duracao = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

/**
 * Uso da máquina: quem esteve nela, quando e por quanto tempo.
 *
 * A seção some quando não há sessão — que é o estado enquanto `Fleet:Sessions:Enabled` estiver
 * desligado no servidor. É de propósito: uma tela vazia prometendo um dado que ninguém ligou é
 * pior que tela nenhuma.
 */
const FleetSessionsPanel: React.FC<{ equipamentId: number }> = ({ equipamentId }) => {
  const [sessions, setSessions] = useState<FleetSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const res = await api.get<FleetSession[]>(
          `/Fleet/equipament/${equipamentId}/sessions?dias=30`
        );
        if (vivo) setSessions(res.data ?? []);
      } catch {
        if (vivo) setSessions([]);
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [equipamentId]);

  if (loading || sessions.length === 0) return null;

  const totalMin = sessions.reduce((acc, s) => acc + s.durationMinutes, 0);

  return (
    <div className="flex flex-col sm:col-span-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Uso nos últimos 30 dias
        </span>
        <span className="text-xs tabular-nums text-gray-600 dark:text-gray-400">
          {sessions.length} sessões · {duracao(totalMin)}
        </span>
      </div>

      <div className="mt-1.5 max-h-64 overflow-y-auto rounded-xl border border-gray-100 dark:border-white/10">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-gray-50 text-gray-700 dark:bg-navy-900 dark:text-gray-300">
            <tr>
              <th className="px-3 py-2 font-semibold">Quem</th>
              <th className="px-3 py-2 font-semibold">Entrou</th>
              <th className="px-3 py-2 font-semibold">Duração</th>
              <th className="px-3 py-2 font-semibold">Onde</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 dark:border-white/10">
                <td className="px-3 py-2 text-navy-700 dark:text-white">
                  {s.employeeName || s.userUpn || s.osUser}
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                  {new Date(s.logonAt).toLocaleString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
                {/* Aproximada e assumida: o fim de uma sessão não é capturável. */}
                <td
                  className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300"
                  title="Estimativa por baixo: conta até o último sinal de vida da máquina"
                >
                  ~{duracao(s.durationMinutes)}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                  {s.lastCity ? (
                    <span className="flex items-center gap-1">
                      <FiMapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                      {s.lastCity}
                      {s.lastRegion ? `/${s.lastRegion}` : ""}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FleetSessionsPanel;
